import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";
import { buildSubcategoryTree, countSubRowsInTree } from "../../utils/providerSubcategoryTree";
import "./ProviderSpecialtiesEditor.scss";

const FALLBACK_IMG =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect fill="#e8f5f3" width="64" height="64" rx="12"/><text x="32" y="38" text-anchor="middle" font-size="22" fill="#0aa9a9">+</text></svg>`
  );

const pickCatId = (c) => String(c?._id ?? c?.id ?? c?.categoryId ?? "").trim();

const extractCategories = (payload) => {
  const d = payload?.data ?? payload;
  return d?.data || d?.items || d?.categories || (Array.isArray(d) ? d : []) || [];
};

const extractSubItems = (payload) => {
  const d = payload?.data ?? payload;
  return d?.items || d?.data || d?.subcategories || (Array.isArray(d) ? d : []) || [];
};

const levelBadge = (level) => {
  const n = Math.max(2, Math.min(5, Number(level || 2)));
  return `L${n}`;
};

const pickSubId = (row) => String(row?.subCategoryId?._id ?? row?.subCategoryId ?? row?._id ?? row?.id ?? "").trim();

function collectSubIdsFromFlatItems(items) {
  const out = [];
  (Array.isArray(items) ? items : []).forEach((row) => {
    const sid = pickSubId(row);
    if (sid) out.push(sid);
  });
  return out;
}

function SubcategoryRows({ nodes, categoryId, selectedIds, onToggleRequest }) {
  if (!Array.isArray(nodes) || !nodes.length) return null;
  return nodes.map((node) => {
    const isSelected = selectedIds.has(node.uiId);
    return (
      <React.Fragment key={node.uiId}>
        <div
          className={`doc_spec_sub_row doc_spec_sub_row--lvl-${Math.max(2, Math.min(5, Number(node.level || 2)))}`}
          style={{ paddingLeft: `${12 + (node.treeDepth || 0) * 18}px` }}
        >
          <label className="doc_spec_sub_label">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleRequest(node.uiId, categoryId, node.name)}
            />
            <span className="doc_spec_level_badge">{levelBadge(node.level)}</span>
            <span className="doc_spec_sub_name">{node.name}</span>
            <span className={`doc_spec_check_mark ${isSelected ? "doc_spec_check_mark--on" : ""}`} aria-hidden="true">
              {isSelected ? "✓" : ""}
            </span>
          </label>
        </div>
        <SubcategoryRows nodes={node.children} categoryId={categoryId} selectedIds={selectedIds} onToggleRequest={onToggleRequest} />
      </React.Fragment>
    );
  });
}

/**
 * Category / subcategory tree (admin-style layering) with checkboxes to edit specialties.
 */
export default function ProviderSpecialtiesEditor({ isHospital }) {
  const categoriesPath = isHospital ? ENDPOINTS.GET_HOSPITAL_CATEGORIES : ENDPOINTS.GET_DOCTOR_CATEGORIES;
  const subcategoriesPath = isHospital ? ENDPOINTS.GET_HOSPITAL_SUBCATEGORIES : ENDPOINTS.GET_DOCTOR_SUBCATEGORIES;
  const selectionsPath = isHospital ? ENDPOINTS.GET_HOSPITAL_SELECTIONS : ENDPOINTS.GET_DOCTOR_SELECTIONS;
  const saveSelectionsPath = isHospital ? ENDPOINTS.CREATE_HOSPITAL_SELECTION : ENDPOINTS.CREATE_DOCTOR_SELECTION;

  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingSelections, setLoadingSelections] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());
  const [subsByCategory, setSubsByCategory] = useState({});
  const [loadingSubsFor, setLoadingSubsFor] = useState(() => new Set());
  const [selectedSubIds, setSelectedSubIds] = useState(() => new Set());
  const [subIdToCategory, setSubIdToCategory] = useState(() => new Map());

  const subsRef = useRef({});
  useEffect(() => {
    subsRef.current = subsByCategory;
  }, [subsByCategory]);

  const seedFromSelectionsList = useCallback((list) => {
    const init = new Set();
    const m = new Map();
    const expand = new Set();
    (Array.isArray(list) ? list : []).forEach((block) => {
      const cid = String(block?.categoryId?._id ?? block?.categoryId ?? "").trim();
      if (cid) expand.add(cid);
      (block?.subCategories || []).forEach((s) => {
        const sid = String(s?.subCategoryId?._id ?? s?.subCategoryId ?? "").trim();
        if (!sid) return;
        init.add(sid);
        if (cid) m.set(sid, cid);
      });
    });
    setSelectedSubIds(init);
    setSubIdToCategory(m);
    setExpandedCategoryIds((prev) => new Set([...prev, ...expand]));
  }, []);

  const loadCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const res = await API.get(categoriesPath, { params: { page: 1, limit: 500 } });
      setCategories(extractCategories(res?.data));
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not load categories", {
        variant: "error",
        title: "Specialties",
      });
      setCategories([]);
    } finally {
      setLoadingCats(false);
    }
  }, [categoriesPath]);

  const loadSelections = useCallback(async () => {
    setLoadingSelections(true);
    try {
      const res = await API.get(selectionsPath);
      const list = res?.data?.selections ?? res?.selections ?? [];
      seedFromSelectionsList(list);
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not load your selections", {
        variant: "error",
        title: "Specialties",
      });
      seedFromSelectionsList([]);
    } finally {
      setLoadingSelections(false);
    }
  }, [selectionsPath, seedFromSelectionsList]);

  useEffect(() => {
    loadCategories();
    loadSelections();
  }, [loadCategories, loadSelections]);

  const ensureSubsLoaded = useCallback(
    async (categoryId) => {
      const cid = String(categoryId);
      if (!cid || subsRef.current[cid]) return;
      setLoadingSubsFor((prev) => new Set(prev).add(cid));
      try {
        const res = await API.get(subcategoriesPath, {
          params: { categoryId: cid, page: 1, limit: 1000 },
        });
        const items = extractSubItems(res?.data);
        setSubsByCategory((prev) => ({ ...prev, [cid]: items }));
        setSubIdToCategory((prev) => {
          const next = new Map(prev);
          items.forEach((row) => {
            const sid = pickSubId(row);
            if (sid) next.set(sid, cid);
          });
          return next;
        });
      } catch (_) {
        setSubsByCategory((prev) => ({ ...prev, [cid]: [] }));
      } finally {
        setLoadingSubsFor((prev) => {
          const next = new Set(prev);
          next.delete(cid);
          return next;
        });
      }
    },
    [subcategoriesPath]
  );

  useEffect(() => {
    if (loadingCats || loadingSelections) return;
    expandedCategoryIds.forEach((cid) => {
      void ensureSubsLoaded(cid);
    });
  }, [loadingCats, loadingSelections, expandedCategoryIds, ensureSubsLoaded]);

  const normalizedCategories = useMemo(
    () =>
      categories
        .map((item) => ({
          id: pickCatId(item),
          name: item?.name || item?.title || item?.categoryName || "Category",
          image: item?.image || item?.categoryImage || "",
        }))
        .filter((c) => c.id),
    [categories]
  );

  const selectedCountByCategory = useMemo(() => {
    const map = new Map();
    selectedSubIds.forEach((sid) => {
      const cid = subIdToCategory.get(sid);
      if (!cid) return;
      map.set(cid, (map.get(cid) || 0) + 1);
    });
    return map;
  }, [selectedSubIds, subIdToCategory]);

  const toggleCategoryExpanded = (categoryId) => {
    const id = String(categoryId);
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildPayloadFrom = useCallback((selSet, catMap) => {
    const map = new Map();
    selSet.forEach((sid) => {
      const cid = catMap.get(sid);
      if (!cid) return;
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(sid);
    });
    return {
      selections: Array.from(map.entries()).map(([categoryId, subCategoryIds]) => ({
        categoryId,
        subCategoryIds,
      })),
    };
  }, []);

  const persistSelections = useCallback(
    async (selSet, catMap, { showSuccess = false } = {}) => {
      const payload = buildPayloadFrom(selSet, catMap);
      if (!payload.selections.length || !payload.selections.some((s) => s.subCategoryIds?.length)) {
        showAlert("Select at least one procedure (subcategory) to save.", { variant: "warning" });
        return false;
      }
      setSaving(true);
      try {
        await API.post(saveSelectionsPath, payload);
        if (showSuccess) showAlert("Your specialties were updated.", { variant: "success" });
        return true;
      } catch (err) {
        showAlert(err?.response?.data?.message || err?.message || "Could not save selections", {
          variant: "error",
          title: "Specialties",
        });
        await loadSelections();
        return false;
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadFrom, loadSelections, saveSelectionsPath]
  );

  const requestToggleSub = useCallback(
    async (subId, categoryId, displayName) => {
      const label = String(displayName || "this procedure").trim().slice(0, 120);
      const ok = await showConfirm(`Change your selection for “${label}”?`, {
        title: "Confirm change",
        confirmText: "Yes, change",
        cancelText: "Cancel",
      });
      if (!ok) return;

      const sid = String(subId);
      const cid = String(categoryId);
      const nextSel = new Set(selectedSubIds);
      const nextMap = new Map(subIdToCategory);
      if (nextSel.has(sid)) {
        nextSel.delete(sid);
        nextMap.delete(sid);
      } else {
        nextSel.add(sid);
        nextMap.set(sid, cid);
      }
      setSelectedSubIds(nextSel);
      setSubIdToCategory(nextMap);
      await persistSelections(nextSel, nextMap);
    },
    [persistSelections, selectedSubIds, subIdToCategory]
  );

  const handleSelectExpanded = useCallback(async () => {
    if (!expandedCategoryIds.size) {
      showAlert("Expand one or more categories first. Select will mark every procedure in those open categories.", {
        variant: "info",
        title: "Specialties",
      });
      return;
    }
    const ok = await showConfirm(
      "Select all procedures in every category you currently have expanded?",
      { title: "Select procedures", confirmText: "Select all", cancelText: "Cancel" }
    );
    if (!ok) return;
    const nextMap = new Map(subIdToCategory);
    const nextSel = new Set(selectedSubIds);
    expandedCategoryIds.forEach((cid) => {
      const items = subsByCategory[cid];
      collectSubIdsFromFlatItems(items).forEach((sid) => {
        nextMap.set(sid, cid);
        nextSel.add(sid);
      });
    });
    setSubIdToCategory(nextMap);
    setSelectedSubIds(nextSel);
    await persistSelections(nextSel, nextMap);
  }, [expandedCategoryIds, persistSelections, selectedSubIds, subIdToCategory, subsByCategory]);

  const handleMergeAllLoaded = useCallback(async () => {
    const keys = Object.keys(subsByCategory || {});
    if (!keys.length) {
      showAlert("No procedures are loaded yet. Expand a category to load its list, then try Merge.", {
        variant: "info",
        title: "Specialties",
      });
      return;
    }
    const ok = await showConfirm(
      "Merge will add every procedure already loaded (all categories you opened) into your selection, keeping your existing picks. Continue?",
      { title: "Merge selections", confirmText: "Merge", cancelText: "Cancel" }
    );
    if (!ok) return;
    const nextMap = new Map(subIdToCategory);
    const nextSel = new Set(selectedSubIds);
    Object.entries(subsByCategory).forEach(([cid, items]) => {
      collectSubIdsFromFlatItems(items).forEach((sid) => {
        nextMap.set(sid, cid);
        nextSel.add(sid);
      });
    });
    setSubIdToCategory(nextMap);
    setSelectedSubIds(nextSel);
    await persistSelections(nextSel, nextMap);
  }, [persistSelections, selectedSubIds, subIdToCategory, subsByCategory]);

  const handleSelectCategory = useCallback(
    async (categoryId, categoryName) => {
      const cid = String(categoryId || "").trim();
      if (!cid) return;
      const items = subsByCategory[cid];
      const subIds = collectSubIdsFromFlatItems(items);
      if (!subIds.length) {
        showAlert("No procedures loaded for this category yet. Please wait a second and try again.", {
          variant: "info",
          title: "Specialties",
        });
        return;
      }
      const ok = await showConfirm(
        `Select all procedures in “${String(categoryName || "this category").trim()}”?`,
        { title: "Select category", confirmText: "Select all", cancelText: "Cancel" }
      );
      if (!ok) return;
      const nextMap = new Map(subIdToCategory);
      const nextSel = new Set(selectedSubIds);
      subIds.forEach((sid) => {
        nextMap.set(sid, cid);
        nextSel.add(sid);
      });
      setSubIdToCategory(nextMap);
      setSelectedSubIds(nextSel);
      await persistSelections(nextSel, nextMap);
    },
    [persistSelections, selectedSubIds, subIdToCategory, subsByCategory]
  );

  const saveSelections = async () => {
    const payload = buildPayloadFrom(selectedSubIds, subIdToCategory);
    if (!payload.selections.length || !payload.selections.some((s) => s.subCategoryIds?.length)) {
      showAlert("Select at least one procedure (subcategory) to save.", { variant: "warning" });
      return;
    }
    const ok = await showConfirm("Save these specialty selections to your profile?", {
      title: "Confirm save",
      confirmText: "Save",
      cancelText: "Cancel",
    });
    if (!ok) return;
    await persistSelections(selectedSubIds, subIdToCategory, { showSuccess: true });
  };

  const busy = loadingCats || loadingSelections;

  return (
    <section className="doc_spec_section">
      <div className="doc_spec_head">
        <h2 className="doc_spec_title">Procedures and specialties</h2>
        <p className="doc_spec_desc">
          These are the categories and services available for you. Selected means this is a service you provide; not
          selected means you do not provide it right now. You can edit anytime and save.
        </p>
        <div className="doc_spec_bulk_row">
          <button type="button" className="doc_spec_bulk_btn doc_spec_bulk_btn--select" onClick={handleSelectExpanded}>
            Select
          </button>
          <button type="button" className="doc_spec_bulk_btn doc_spec_bulk_btn--merge" onClick={handleMergeAllLoaded}>
            Merge
          </button>
          <span className="doc_spec_bulk_hint">
            Select: all procedures in expanded categories. Merge: add every procedure already loaded into your selection.
          </span>
        </div>
      </div>

      {busy ? (
        <div className="doc_spec_muted">Loading categories and your current selections…</div>
      ) : (
        <>
          <div className="doc_spec_list">
            {normalizedCategories.map((cat) => {
              const isOpen = expandedCategoryIds.has(cat.id);
              const flat = subsByCategory[cat.id];
              const tree = Array.isArray(flat) ? buildSubcategoryTree(flat, cat.id) : [];
              const rowCount = tree.length ? countSubRowsInTree(tree) : 0;
              const loadingThis = loadingSubsFor.has(cat.id);

              return (
                <div className="doc_spec_cat_card" key={cat.id}>
                  <div className="doc_spec_cat_head">
                    <button
                      type="button"
                      className="doc_spec_expand_btn"
                      onClick={() => toggleCategoryExpanded(cat.id)}
                      aria-expanded={isOpen}
                      aria-controls={`doc-spec-sub-${cat.id}`}
                    >
                      <span className={`doc_spec_chevron ${isOpen ? "doc_spec_chevron--open" : ""}`} aria-hidden>
                        ▸
                      </span>
                    </button>
                    <div className="doc_spec_cat_img_wrap">
                      <img src={cat.image || FALLBACK_IMG} alt="" className="doc_spec_cat_img" />
                    </div>
                    <div className="doc_spec_cat_main">
                      <h3 className="doc_spec_cat_name">{cat.name}</h3>
                      {!loadingThis ? (
                        <div className="doc_spec_cat_meta">
                          {selectedCountByCategory.get(cat.id) || 0} selected
                          {isOpen && rowCount > 0 ? ` • ${rowCount} procedure${rowCount === 1 ? "" : "s"}` : ""}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="doc_spec_cat_select_btn"
                      onClick={() => handleSelectCategory(cat.id, cat.name)}
                      disabled={loadingThis || rowCount === 0}
                      title="Select all procedures in this category"
                    >
                      Select all
                    </button>
                  </div>

                  {isOpen ? (
                    <div className="doc_spec_cat_body" id={`doc-spec-sub-${cat.id}`}>
                      {loadingThis ? (
                        <div className="doc_spec_muted doc_spec_muted--pad">Loading procedures…</div>
                      ) : tree.length === 0 ? (
                        <div className="doc_spec_muted doc_spec_muted--pad">No procedures listed for this category.</div>
                      ) : (
                        <div className="doc_spec_sub_wrap">
                          <SubcategoryRows nodes={tree} categoryId={cat.id} selectedIds={selectedSubIds} onToggleRequest={requestToggleSub} />
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="doc_spec_save_row">
            <button type="button" className="doc_spec_save_btn" onClick={saveSelections} disabled={saving}>
              {saving ? "Saving specialties…" : "Save specialties"}
            </button>
            <span className="doc_spec_count">{selectedSubIds.size} selected</span>
          </div>
        </>
      )}
    </section>
  );
}
